import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs/Observable';

import { Contact } from './contact.model';
import { Communication } from '../../shared/communications/communication.model';

import * as _ from 'lodash';
import { Company } from '../companies/company.model';
import { EventBB } from '../events/event.model';
import { ContactCreateComponent } from './create/contact-create.component';
import { Task } from '../../shared/tasks/task.model';
import { Attachment } from '../../shared/attachments/attachment.model';

@Injectable()
// should be Contacts!
export class ContactService implements OnDestroy {

  keepSubscriptionsAlive = true;

  constructor(private http: HttpClient) { }

  ngOnDestroy() {
    this.keepSubscriptionsAlive = false;
  }

  addCommunicationToContact(contact: Contact, communication: Communication) {

    // create a clone and a spoof company and use the id - otherwise we get a json circular reference
    const clone = _.cloneDeep(contact);

    const spoofCommunication = new Communication();
    spoofCommunication._id = communication._id;

    // push the spoofed company to the team member
    clone.communications.unshift(spoofCommunication);

    // console.log('saving company to teamMember');
    this.saveContact(clone);

  }

  addEventToContact(contact: Contact, eventbb: EventBB) {

    // create a clone and a spoof company and use the id - otherwise we get a json circular reference
    const clone: Contact = _.cloneDeep(contact);

    const spoofEventBB = new EventBB();
    spoofEventBB._id = eventbb._id;

    // push the spoofed company to the team member
    clone.eventsAttended.unshift(spoofEventBB);

    // console.log('saving company to teamMember');
    this.saveContact(clone);

  }

  addTaskToContact(contact: Contact, task: Task) {

    // create a clone and a spoof company and use the id - otherwise we get a json circular reference
    const clone: Contact = _.cloneDeep(contact);

    const spoofTask = new Task();
    spoofTask._id = task._id;

    // push the spoofed company to the team member
    clone.tasks.unshift(spoofTask);

    // console.log('saving company to teamMember');
    this.saveContact(clone);

  }

  removeEventFromContact(contact: Contact, eventBb: EventBB) {

    if (contact !== undefined) {

      // filter out events
      contact.eventsAttended = contact.eventsAttended.filter(function (eve) {

        // we don't have the full id at this point, so just copy it to a string and remove
        const eventId: string = String(eve);
        return eventId !== eventBb._id;

      });

      // finally save the event
      this.saveContact(contact);

    }

  }


  addContactToCompany(contact: Contact, company: Company) {

    // create a clone contact and a spoof company and use the id - otherwise we get a json circular reference
    const clone = _.cloneDeep(contact);

    const spoofCompany = new Company();
    spoofCompany._id = company._id;

    // push the spoofed company to the team member
    clone.companies.push(spoofCompany);

    // console.log('saving company to teamMember');
    this.saveContact(clone);

  }

  addAttachmentToContact(contact: Contact, attachment: Attachment) {

    // create a clone and a spoof company and use the id - otherwise we get a json circular reference
    const clone: Contact = _.cloneDeep(contact);

    const spoofAttachment = new Attachment();
    spoofAttachment._id = attachment._id;

    // push the spoofed company to the team member
    clone.attachments.unshift(spoofAttachment);

    // console.log('saving company to teamMember');
    this.saveContact(clone);

  }

  // main save contact
  saveContact(contact: Contact) {

    this.editContact(contact)
      .takeWhile(() => this.keepSubscriptionsAlive)
      .subscribe(
        res => {
          console.log(res);
        },
        error => {
          console.log(error);
        }
      );
  }

  addReferralToContact(contact: Contact, company: Company) {

    // create a clone contact and a spoof company and use the id - otherwise we get a json circular reference
    const clone = _.cloneDeep(contact);

    const spoofCompany = new Company();
    spoofCompany._id = company._id;

    // push the spoofed company to the team member contact
    clone.referrals.unshift(spoofCompany);

    // then save it
    this.saveContact(clone);

  }

  getContacts(): Observable<Contact[]> {
    return this.http.get<Contact[]>('/api/contacts');
  }

  getListOfContacts(): Observable<any> {
    // console.log('i am here');
    return this.http.get<any>(`/api/contacts/list`);
  }


  findContacts(searchString: string): Observable<Contact[]> {

    if (searchString === undefined) { searchString = ' '; }

    // console.log(`/api/contacts/find/${searchString}`);
    // see https://github.com/surfer77/mongoose-string-query for full API!
    return this.http.get<Contact[]>(`/api/contacts/find/${searchString}`, {
      headers: { ignoreLoadingBar: '' }
    }).map((contacts: Contact[]) => {
      return contacts;
    }).catch(this.handleError);

  }


  findTopNContacts(firstName: string = '', lastName: string = '', limit: number = 5): Observable<Contact[]> {

    if (firstName === undefined || firstName === '') { firstName = ' '; }
    if (lastName === undefined || lastName === '') { lastName = ' '; }
    // console.log(`/api/contacts/find/${searchString}`);
    // see https://github.com/surfer77/mongoose-string-query for full API!
    return this.http.get<Contact[]>(`/api/contacts/find/top/${firstName}/${lastName}/${limit}`, {
      // headers: { ignoreLoadingBar: '' }
    }).map((contacts: Contact[]) => {
      return contacts;
    }).catch(this.handleError);

  }

  findTopNAmbassadorContacts(firstName: string = '', lastName: string = '', limit: number = 5): Observable<Contact[]> {

    if (firstName === undefined || firstName === '') { firstName = ' '; }
    if (lastName === undefined || lastName === '') { lastName = ' '; }
    // console.log(`/api/contacts/find/${searchString}`);
    // see https://github.com/surfer77/mongoose-string-query for full API!
    return this.http.get<Contact[]>(`/api/contacts/find/topAmbassadors/${firstName}/${lastName}/${limit}`, {
      // headers: { ignoreLoadingBar: '' }
    }).map((contacts: Contact[]) => {
      return contacts;
    }).catch(this.handleError);

  }

  // find contacts using a filter query string
  findContactsByQuery(queryString: string): Observable<Contact[]> {
    return this.http.get<Contact[]>(`/api/contacts/findByQuery/${queryString}`, {
      // headers: { ignoreLoadingBar: '' }
    }).map((companies: Contact[]) => {
      return companies;
    }).catch(this.handleError);

  }

  searchContacts(searchString: string): Observable<Contact[]> {

    console.log('/api/contacts/search?' + searchString);
    // see https://github.com/surfer77/mongoose-string-query for full API!
    return this.http.get<Contact[]>('/api/contacts/search?' + searchString, {
      headers: { ignoreLoadingBar: '' }
    }).map((contacts: Contact[]) => {
      return contacts;
    }).catch(this.handleError);

  }

  countContacts(): Observable<number> {
    return this.http.get<number>('/api/contacts/count');
  }

  addContact(contact: Contact): Observable<Contact> {
    // console.log(contact);
    return this.http.post<Contact>('/api/contact', contact);
  }

  getContactFromId(id: string): Observable<Contact> {
    return this.http.get<Contact>(`/api/contact/${id}`);
    // ).get(requireAuth, contactCtrl.get);
  }

  getContactsFromIds(contactIds: string): Observable<Contact[]> {
    return this.http.get<Contact[]>(`/api/contacts/listByIds/${contactIds}`, {
      headers: { ignoreLoadingBar: '' }
    });
    // ).get(requireAuth, contactCtrl.get);
  }

  getContactsCreatedFromTo(from: string, to: string): Observable<Contact[]> {
    return this.http.get<Contact[]>(`/api/contacts/byDate/${from}/${to}`);
  }

  getDoNotContact(): Observable<Contact[]> {
    return this.http.get<Contact[]>(`/api/contacts/doNotContact`);
  }

  getContact(contact: Contact): Observable<Contact> {
    return this.http.get<Contact>(`/api/contact/${contact.reference}`);
  }

  editContact(contact: Contact): Observable<string> {
    return this.http.put(`/api/contact/${contact._id}`, contact, { responseType: 'text' });
  }

  deleteContact(contact: Contact): Observable<string> {
    return this.http.delete(`/api/contact/${contact._id}`, { responseType: 'text' });
  }

  private handleError(error: Response) {
    return Observable.throw(error.statusText);
  }

}



// WEBPACK FOOTER //
// ./client/app/features/contacts/contact.service.ts